import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Target, Globe, Award, Shield, Lightbulb, Heart, Download, FileText } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { aboutContent, stats } from '../mockData';

const values = [
  { icon: Award, title: 'Quality', desc: 'We maintain the highest standards in every project, ensuring reliable and efficient automation solutions.' },
  { icon: Lightbulb, title: 'Innovation', desc: 'We stay at the forefront of automation technology to deliver cutting-edge solutions for our clients.' },
  { icon: Shield, title: 'Integrity', desc: 'We build lasting relationships through transparency, honesty, and ethical business practices.' },
  { icon: Heart, title: 'Customer Focus', desc: 'We prioritize client satisfaction with tailored solutions and dedicated support throughout the project lifecycle.' },
];

export const AboutPage = () => {
  const storySections = aboutContent.story
    .split('\n\n')
    .map((section) => section.trim())
    .filter(Boolean);

  const storyIntro = storySections[0] || '';
  const storyClosing = storySections[storySections.length - 1] || '';
  const storyTimeline = storySections
    .slice(1, -1)
    .flatMap((section) => section.split('\n'))
    .map((item) => item.trim().replace(/^[^A-Za-z0-9]+/, ''))
    .filter(Boolean);

  return (
    <>
      <SEOHead
        title="About Hexalogix Automation | Automation Experts UAE"
        description="Founded in 2019, Hexalogix Automation delivers industrial automation solutions across UAE, Africa & Middle East. Expert team in PLC, panels & marine automation."
        keywords="about hexalogix, automation company UAE, industrial automation experts, control panel manufacturer, automation engineers Middle East"
      />

      <div className="min-h-screen">

        {/* ═══════════════════ PAGE HERO ═══════════════════ */}
        <section className="relative pt-16 md:pt-[106px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=1920&h=600&fit=crop&q=80"
              alt="About Hexalogix Automation"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1E]/90 via-[#0A0F1E]/70 to-[#0A0F1E]/40" />
          </div>
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#0057B8]" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-gray-300">About</span>
            </nav>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
              <span className="text-[#0057B8] text-lg font-semibold tracking-widest uppercase">Who We Are</span>
              <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight mb-4 whitespace-nowrap">
              About Hexalogix Automation
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
              Driving progress through innovation, precision, and dedication in industrial automation since 2019.
            </p>
          </div>
        </section>

        {/* ═══════════════════ STATS ═══════════════════ */}
        <section className="bg-[#F0F4F9] border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-200">
              {stats.map((stat, i) => (
                <div key={i} className="py-8 px-6 md:px-8 text-center md:text-left">
                  <div className="text-3xl md:text-4xl font-black text-[#0057B8] mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-500 uppercase tracking-widest font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════ OUR STORY ═══════════════════ */}
        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                  <span className="text-[#0057B8] text-lg font-semibold tracking-widest uppercase">Our Story</span>
                  <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E] leading-tight mb-6">
                  Built on Engineering Excellence
                </h2>
                <div className="mb-6 space-y-4 text-lg text-gray-600 leading-relaxed">
                  <p>{storyIntro}</p>
                  <ul className="space-y-3 pl-6 list-disc marker:text-[#0057B8]">
                    {storyTimeline.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <p>{storyClosing}</p>
                </div>
                <Link to="/contact">
                  <button className="inline-flex items-center gap-2 bg-[#0057B8] hover:bg-[#003D82] text-white font-semibold px-7 py-3.5 text-base tracking-wide transition-all duration-200 group">
                    Talk to an Engineer
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
              </div>

              <div className="relative">
                <img
                  src={aboutContent.image}
                  alt="Hexalogix Team"
                  className="w-full h-[480px] object-cover shadow-2xl"
                />
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-[#0057B8] -z-10" />
                <div className="absolute top-6 right-6 bg-[#0A0F1E] text-white px-5 py-4 shadow-xl">
                  <div className="text-2xl font-black text-[#0057B8]">2019</div>
                  <div className="text-sm text-gray-400 uppercase tracking-widest mt-1">Established</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════ MISSION & VISION ═══════════════════ */}
        <section className="bg-[#F0F4F9] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-14">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                <span className="text-[#0057B8] text-lg font-semibold tracking-widest uppercase">Our Purpose</span>
                <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E]">Mission &amp; Vision</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-gray-200 hover:border-[#0057B8]/50 bg-white hover:shadow-lg p-8 transition-all duration-300 group">
                <div className="w-12 h-12 bg-[#EBF4FF] border border-[#0057B8]/20 flex items-center justify-center mb-6 group-hover:bg-[#0057B8] transition-colors">
                  <Target className="w-6 h-6 text-[#0057B8] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-black text-[#0A0F1E] mb-4">Our Mission</h3>
                <p className="text-gray-500 leading-relaxed">{aboutContent.mission}</p>
              </div>

              <div className="border border-gray-200 hover:border-[#0057B8]/50 bg-white hover:shadow-lg p-8 transition-all duration-300 group">
                <div className="w-12 h-12 bg-[#EBF4FF] border border-[#0057B8]/20 flex items-center justify-center mb-6 group-hover:bg-[#0057B8] transition-colors">
                  <Globe className="w-6 h-6 text-[#0057B8] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-black text-[#0A0F1E] mb-4">Our Vision</h3>
                <p className="text-gray-500 leading-relaxed">{aboutContent.vision}</p>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════ CORE VALUES ═══════════════════ */}
        <section className="bg-[#F0F4F9] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-14">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
                <span className="text-[#0057B8] text-lg font-semibold tracking-widest uppercase">What Drives Us</span>
                <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-[#0A0F1E]">Our Core Values</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, i) => {
                const Icon = value.icon;
                return (
                  <div key={i} className="bg-white border border-gray-100 hover:border-[#0057B8]/30 hover:shadow-lg p-8 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-[#EBF4FF] border border-[#0057B8]/20 flex items-center justify-center mb-5 group-hover:bg-[#0057B8] transition-colors">
                      <Icon className="w-6 h-6 text-[#0057B8] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-black text-[#0A0F1E] mb-3 text-lg">{value.title}</h3>
                    <p className="text-gray-500 text-base leading-relaxed">{value.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══════════════════ BROCHURE DOWNLOAD ═══════════════════ */}
        <section className="bg-[#F0F4F9] py-14 border-y border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 bg-[#0057B8]/10 border border-[#0057B8]/20 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-7 h-7 text-[#0057B8]" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-[#0A0F1E] mb-1">
                    Company Brochure
                  </h3>
                  <p className="text-gray-500 text-base max-w-md">
                    Download our complete company profile and service catalogue. Learn about our full capabilities, past projects, and certifications.
                  </p>
                </div>
              </div>
              <a
                href="/Hexalogix-Brochure.pdf"
                download="Hexalogix-Automation-Brochure.pdf"
                className="inline-flex items-center gap-2 bg-[#0057B8] hover:bg-[#003D82] text-white font-bold px-8 py-4 text-base tracking-wide transition-all duration-200 hover:shadow-lg hover:shadow-[#0057B8]/30 group flex-shrink-0"
              >
                <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                Download Brochure (PDF)
              </a>
            </div>
          </div>
        </section>

        {/* ═══════════════════ CTA ═══════════════════ */}
        <section className="relative bg-[#0057B8] py-20 md:py-24 overflow-hidden">
          <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-[#003D82]/40 -skew-x-12 translate-x-16" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-10">
            <div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">
                Ready to Partner with Us?
              </h2>
              <p className="text-blue-200 max-w-lg">
                Let's discuss how Hexalogix Automation can transform your industrial operations.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
              <Link to="/contact">
                <button className="inline-flex items-center gap-2 bg-white text-[#0057B8] hover:bg-gray-100 font-bold px-8 py-4 text-base tracking-wide transition-all duration-200 group">
                  Request a Consultation
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              <a
                href="/Hexalogix-Brochure.pdf"
                download="Hexalogix-Automation-Brochure.pdf"
                className="inline-flex items-center gap-2 border-2 border-white/50 hover:border-white bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-4 text-base tracking-wide transition-all duration-200 group"
              >
                <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                Our Brochure
              </a>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

